import { useRef, useState, useCallback, useEffect } from 'react';
import ReactECharts from 'echarts-for-react';

const DRAG_THRESHOLD_PX = 6;

export type DragRect = { left: number; top: number; width: number; height: number };
export type PendingSelection = { ids: string[]; x: number; y: number };

export type DragRectSelectArgs<P> = {
  points: P[];
  getId: (p: P) => string;
  /** Convert a point to chart pixel coords (relative to the chart container). Return null if not on-canvas. */
  getPixel: (p: P, instance: any) => [number, number] | null;
  containerRef: React.RefObject<HTMLDivElement>;
  echartsRef: React.RefObject<ReactECharts>;
  onPointerHover: (id: string | null) => void;
};

export type DragRectSelectApi = {
  dragRect: DragRect | null;
  pending: PendingSelection | null;
  isDragging: boolean;
  clearPending: () => void;
  setPendingIds: (ids: string[]) => void;
  /** Wire these to the chart container. */
  handlers: {
    onMouseDown: (e: React.MouseEvent<HTMLDivElement>) => void;
    onMouseMove: (e: React.MouseEvent<HTMLDivElement>) => void;
    onMouseUp: () => void;
    onMouseLeave: () => void;
  };
};

/**
 * macOS-style drag-rectangle selection helper for ECharts scatter plots.
 *
 * Below DRAG_THRESHOLD_PX of movement, the gesture passes through (so single-click toggling still works).
 * Above the threshold, mousedown→mouseup paints a rectangle; on release every point whose pixel position
 * falls inside the rectangle is "pending" — the caller renders a small Add/Discard modal at the release point.
 */
export function useDragRectSelect<P>({
  points, getId, getPixel, containerRef, echartsRef, onPointerHover,
}: DragRectSelectArgs<P>): DragRectSelectApi {
  type DragState = { startX: number; startY: number; currX: number; currY: number; dragging: boolean };
  const dragRef = useRef<DragState | null>(null);
  const [, setVersion] = useState(0);
  const [pending, setPending] = useState<PendingSelection | null>(null);

  // Whenever points change (filter/mode), drop any stale pending modal.
  useEffect(() => { setPending(null); }, [points]);

  const handleMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.button !== 0) return;
    const rect = containerRef.current?.getBoundingClientRect();
    if (!rect) return;
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    dragRef.current = { startX: x, startY: y, currX: x, currY: y, dragging: false };
  };

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!dragRef.current) return;
    const rect = containerRef.current?.getBoundingClientRect();
    if (!rect) return;
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    dragRef.current.currX = x;
    dragRef.current.currY = y;
    if (!dragRef.current.dragging) {
      const dx = x - dragRef.current.startX;
      const dy = y - dragRef.current.startY;
      if (Math.hypot(dx, dy) > DRAG_THRESHOLD_PX) {
        dragRef.current.dragging = true;
        const instance = echartsRef.current?.getEchartsInstance();
        instance?.dispatchAction({ type: 'hideTip' });
        onPointerHover(null);
      }
    }
    if (dragRef.current.dragging) setVersion((v) => v + 1);
  };

  const handleMouseUp = useCallback(() => {
    const d = dragRef.current;
    if (!d) return;
    if (!d.dragging) { dragRef.current = null; return; }
    const minX = Math.min(d.startX, d.currX);
    const maxX = Math.max(d.startX, d.currX);
    const minY = Math.min(d.startY, d.currY);
    const maxY = Math.max(d.startY, d.currY);
    const instance = echartsRef.current?.getEchartsInstance();
    const ids = new Set<string>();
    if (instance) {
      for (const p of points) {
        const px = getPixel(p, instance);
        if (!px) continue;
        if (px[0] >= minX && px[0] <= maxX && px[1] >= minY && px[1] <= maxY) {
          ids.add(getId(p));
        }
      }
    }
    const releaseX = d.currX;
    const releaseY = d.currY;
    dragRef.current = null;
    setVersion((v) => v + 1);
    const uniqueIds = Array.from(ids);
    if (uniqueIds.length > 0) setPending({ ids: uniqueIds, x: releaseX, y: releaseY });
    else setPending(null);
  }, [points, getPixel, getId, echartsRef]);

  const handleMouseLeave = () => {
    if (dragRef.current?.dragging) handleMouseUp();
    else dragRef.current = null;
  };

  const dragRect: DragRect | null = dragRef.current?.dragging
    ? {
        left: Math.min(dragRef.current.startX, dragRef.current.currX),
        top: Math.min(dragRef.current.startY, dragRef.current.currY),
        width: Math.abs(dragRef.current.currX - dragRef.current.startX),
        height: Math.abs(dragRef.current.currY - dragRef.current.startY),
      }
    : null;

  return {
    dragRect,
    pending,
    isDragging: Boolean(dragRef.current?.dragging),
    clearPending: () => setPending(null),
    setPendingIds: (ids: string[]) => setPending((curr) => curr ? { ...curr, ids } : null),
    handlers: {
      onMouseDown: handleMouseDown,
      onMouseMove: handleMouseMove,
      onMouseUp: handleMouseUp,
      onMouseLeave: handleMouseLeave,
    },
  };
}

/** Clamp the floating Add/Discard modal so it stays fully inside the chart. */
export function clampModalX(x: number, containerWidth?: number): number {
  if (!containerWidth) return x;
  const modalWidth = 280;
  return Math.max(8, Math.min(containerWidth - modalWidth - 8, x + 8));
}
export function clampModalY(y: number, containerHeight?: number): number {
  if (!containerHeight) return y;
  const modalHeight = 56;
  return Math.max(8, Math.min(containerHeight - modalHeight - 8, y + 8));
}
